import { StatusIndicator } from "@/pages/mill/overview-2/components/StatusIndicator";
import { HoursProgressBar } from "@/shared/components/HoursProgressBar";
import FactoryIcon from "@mui/icons-material/Factory";
import { Box, Card, Divider, Paper, Typography } from "@mui/material";
import {
	HeatEventSummary,
	MillSalesSummary,
	ProductionEventSummary,
} from "../models/SalesOverview";
import { CumulativeTonsChart } from "./CumulativeTonsChart";
import { HeatsBySection } from "./heatsBySection/HeatsBySection";
import { MonthlyMetricsPanel } from "./MonthlyMetricsPanel";
import { MonthlyProgressBar } from "./MonthlyProgressBar";
import { SectionPanel } from "./SectionPanel";

type Props = {
	data: MillSalesSummary;
	events: ProductionEventSummary[];
	heatsByEvent: HeatEventSummary[];
};

export const MillStatusCard = ({ data, events, heatsByEvent }: Props) => {
	if (data.IsPlaceholder) {
		return (
			<Paper
				elevation={0}
				sx={{
					p: 2,
					height: "100%",
					minHeight: 520,
					border: "1px dashed #bbb",
					borderRadius: 1,
					backgroundColor: "#fafafa",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					color: "#888",
				}}
			>
				<FactoryIcon sx={{ fontSize: 56, color: "#c0c0c0", mb: 2 }} />
				<Typography variant="h6" sx={{ fontWeight: 600, color: "#666" }}>
					{data.MillName}
				</Typography>
				<Typography
					variant="body2"
					sx={{ mt: 1, textAlign: "center", maxWidth: 240 }}
				>
					Fabrication metrics will appear here once the data source is
					connected.
				</Typography>
				<Typography
					variant="caption"
					sx={{ mt: 2, color: "#aaa", fontStyle: "italic" }}
				>
					Coming soon
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2,
				height: "100%",
				border: "1px solid #ddd",
				borderLeft: `4px solid ${data.IsInDelay ? "#C62828" : "#2E7D32"}`,
				borderRadius: 1,
				backgroundColor: "#fff",
				display: "flex",
				flexDirection: "column",
				gap: 1,
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					{data.MillName}
				</Typography>
				<StatusIndicator isInDelay={data.IsInDelay} />
			</Box>

			<Box
				sx={{
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					gap: 1,
					"& > *": { flex: 1, minWidth: 0 },
				}}
			>
				<SectionPanel
					sectionId={data.CurrentSectionId}
					sectionName={data.CurrentSectionName}
					rollingStart={data.CurrentRollingStart}
					endTime={data.CurrentEventEnd}
					schedFinishedTons={data.CurrentSchedFinishedTons}
					actualFinishedTons={data.CurrentActualFinishedTons}
					schedFinishedYield={data.CurrentSchedFinishedYield}
					budgetTPH={data.CurrentBudgetTPH}
					heatName={data.CurrentHeatName}
					gradeName={data.CurrentGradeName}
					isCurrent={true}
				/>

				<SectionPanel
					sectionId={data.NextSectionId}
					sectionName={data.NextSectionName}
					endTime={data.NextEventEnd}
					rollingStart={data.NextEventStart}
					schedFinishedTons={data.NextSchedFinishedTons}
					schedFinishedYield={data.NextSchedFinishedYield}
					budgetTPH={data.NextBudgetTPH}
					isCurrent={false}
				/>
			</Box>

			<Card sx={{ p: 1, overflow: "visible" }} elevation={3}>
				<HoursProgressBar
					eventStart={data.CurrentEventStart}
					scheduledEnd={data.CurrentEventEnd}
					projectedEnd={data.CurrentProjectedRollOut}
					scheduledHours={data.CurrentScheduledHours}
					currentHours={data.CurrentActualHours}
					remainingHours={data.CurrentRemainingHours}
					varianceHours={data.CurrentVarianceHours}
				/>
			</Card>

			<Divider>
				<Typography
					variant="caption"
					sx={{ color: "#999", fontWeight: 600, letterSpacing: 0.5 }}
				>
					MONTHLY SUMMARY
				</Typography>
			</Divider>

			<Box sx={{ display: "flex", gap: 1 }}>
				<MonthlyMetricsPanel
					variant="actual"
					title="MTD · Actual"
					finishedTons={data.MTDActualFinishedTons}
					avgYield={data.ActualAvgFinishedYield}
					avgTph={data.ActualAvgTPH}
				/>
				<MonthlyMetricsPanel
					variant="scheduled"
					title="Month · Scheduled"
					finishedTons={data.MonthScheduledFinishedTons}
					avgYield={data.ScheduledAvgFinishedYield}
					avgTph={data.ScheduledAvgTPH}
				/>
			</Box>

			<MonthlyProgressBar
				actualTons={data.MTDActualFinishedTons}
				targetTons={data.MonthScheduledFinishedTons}
			/>

			<Divider />

			<CumulativeTonsChart events={events} millId={data.MillId} />

			<Divider />

			<HeatsBySection events={events} heatsByEvent={heatsByEvent} />
		</Paper>
	);
};
